/**
 * Conditional Logging Utility
 *
 * Provides environment-aware logging that respects LOG_LEVEL and DEBUG_MODE.
 * - Development: All logs visible by default
 * - Production: Only errors unless DEBUG mode enabled
 *
 * Usage:
 *   import { createLogger } from '../utils/logger';
 *   const logger = createLogger('ComponentName');
 *   logger.debug('Detailed info');
 *   logger.info('General info');
 *   logger.warn('Warning');
 *   logger.error('Error');
 */

import { getEnv } from './runtimeConfig';

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const DEBUG_MODE = getEnv('REACT_APP_DEBUG') === 'true';
const LOG_LEVEL = getEnv('REACT_APP_LOG_LEVEL') || 'warn';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

class Logger {
  private namespace: string;
  private currentLevel: number;

  constructor(namespace: string) {
    this.namespace = namespace;
    this.currentLevel = LEVELS[LOG_LEVEL as LogLevel] ?? LEVELS.warn;
  }

  private shouldLog(level: LogLevel): boolean {
    return IS_DEVELOPMENT || DEBUG_MODE || LEVELS[level] >= this.currentLevel;
  }

  debug(...args: any[]) {
    if (this.shouldLog('debug')) {
      console.log(`[${this.namespace}]`, ...args);
    }
  }

  info(...args: any[]) {
    if (this.shouldLog('info')) {
      console.info(`[${this.namespace}]`, ...args);
    }
  }

  warn(...args: any[]) {
    if (this.shouldLog('warn')) {
      console.warn(`[${this.namespace}]`, ...args);
    }
  }

  error(...args: any[]) {
    if (this.shouldLog('error')) {
      console.error(`[${this.namespace}]`, ...args);
    }
  }

  // Timing utility
  time(label: string) {
    if (this.shouldLog('debug')) {
      console.time(`[${this.namespace}] ${label}`);
    }
  }

  timeEnd(label: string) {
    if (this.shouldLog('debug')) {
      console.timeEnd(`[${this.namespace}] ${label}`);
    }
  }
}

export function createLogger(namespace: string): Logger {
  return new Logger(namespace);
}
